import { Box, Typography } from "@mui/material";
import type { DateTime } from "luxon";
import type { FC } from "react";
import { NEUTRAL } from "../../../theme/tokens";
import { useMobile } from "../../../utils/utilFunctions";
import { MultiSizePopup } from "../../UIToolkit/Popups/MultiSizePopup/MultiSizePopup";
import { MultiSizePopupState } from "../../UIToolkit/Popups/MultiSizePopup/utils";
import { useAgentHistory } from "../hooks/useAgentHistory";
import { AcceptedNursesCards } from "./AcceptedNursesCards";
import { ActionsAcrodions } from "./ActionsAcrodions";
import { ActionsBoxTile } from "./ActionsBoxTile";
import { MobileActionsTitle } from "./MobileActionsTitle";

interface FillShiftModalProps {
	open: boolean;
	onClose: () => void;
	requestId: string;
	shift_start: DateTime;
	shift_end: DateTime;
	displayFullDate?: boolean;
}

export const FillShiftModal: FC<FillShiftModalProps> = ({ open, onClose, requestId, shift_start, shift_end, displayFullDate }) => {
	const { isMobile } = useMobile();
	const shiftTime = `${shift_start.toFormat("h:mm a")} - ${shift_end.toFormat("h:mm a")}`;
	const shiftDateFormatted = shift_start.toFormat("EEE, MMM d");

	const {
		data: allActions,
		isLoading,
		agentStatus,
		isLoadingMetadata,
		totalNeededNurses,
		isAnyMutationPending,
		pauseByRequestMutation,
		resumeByRequestMutation,
	} = useAgentHistory({
		requestId,
	});

	const neededNurses = totalNeededNurses;
	const isLoadingContent = isLoading || isLoadingMetadata;
	const bodyContent = (
		<Box sx={{ width: "50vw", maxWidth: "50vw", height: "60vh", maxHeight: "70vh", m: 2 }}>
			<Box sx={{ overflowY: "auto", flex: 1 }}>
				{isLoadingContent ? (
					<Box sx={{ py: 4, textAlign: "center" }}>
						<Typography variant="body2" sx={{ color: NEUTRAL[500] }}>
							Loading...
						</Typography>
					</Box>
				) : (
					<>
						<ActionsBoxTile
							shiftDateFormatted={shiftDateFormatted}
							shiftTime={shiftTime}
							neededNurses={neededNurses}
							agentStatus={agentStatus}
							pauseByRequestMutation={pauseByRequestMutation}
							resumeByRequestMutation={resumeByRequestMutation}
							isAnyMutationPending={isAnyMutationPending}
							isLoadingMetadata={isLoadingMetadata}
							allActions={allActions ?? []}
						/>
						<AcceptedNursesCards allActions={allActions ?? []} />
						<ActionsAcrodions actions={allActions ?? []} requestId={requestId} displayFullDate={displayFullDate} />
					</>
				)}
			</Box>
		</Box>
	);

	if (isMobile) {
		if (isLoadingContent) {
			return (
				<Box sx={{ py: 4, textAlign: "center" }}>
					<Typography variant="body2" sx={{ color: NEUTRAL[500] }}>
						Loading...
					</Typography>
				</Box>
			);
		}
		return (
			<>
				{agentStatus && (
					<MobileActionsTitle
						allActions={allActions ?? []}
						neededNurses={neededNurses}
						agentStatus={agentStatus}
						pauseByRequestMutation={pauseByRequestMutation}
						resumeByRequestMutation={resumeByRequestMutation}
						isAnyMutationPending={isAnyMutationPending}
						isLoadingMetadata={isLoadingMetadata}
					/>
				)}
				<AcceptedNursesCards allActions={allActions ?? []} />
				<ActionsAcrodions actions={allActions ?? []} requestId={requestId} />
			</>
		);
	}

	return (
		<MultiSizePopup
			open={open}
			title={"Fill Shift"}
			onClose={onClose}
			bodyRenderer={(currentSizeState) => (currentSizeState === MultiSizePopupState.MAXIMIZED ? bodyContent : null)}
			disableMinimize
			data-testid="fill-shift-modal"
		/>
	);
};
